import { Head } from "@inertiajs/react"
import { Mail, Phone, Smartphone } from "lucide-react"

import AppLayout from "@/layouts/app-layout"
import { companyPhoneBookIndexPath, companyResourcesPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface Employee {
  id: number
  name: string
  job_title: string | null
  cell_phone: string | null
  home_phone: string | null
  work_phone: string | null
  email: string | null
}

interface CompanyGroup {
  id: number
  name: string
  employees: Employee[]
}

interface Props {
  groups: CompanyGroup[]
  total_employees: number
}

/* ── Helpers ────────────────────────────────────────────── */

function formatPhone(phone: string | null): string {
  if (!phone) return ""
  const digits = phone.replace(/\D/g, "")
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return phone
}

/* ── Main Component ────────────────────────────────────── */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Company", href: companyResourcesPath() },
  { title: "Phone Book", href: companyPhoneBookIndexPath() },
]

export default function PhoneBookIndex({ groups, total_employees }: Props) {
  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Phone Book" />

      <div className="p-6">
        <div className="text-hpi-med-blue mb-4 text-sm">
          {total_employees} employee{total_employees !== 1 ? "s" : ""}
        </div>

        {groups.length === 0 ? (
          <div className="border-hpi-light-gray text-hpi-med-blue rounded-lg border bg-white p-8 text-center">
            No employees found.
          </div>
        ) : (
          <div className="space-y-6">
            {groups.map((group) => (
              <div key={group.id}>
                <div className="bg-hpi-dark-blue mb-3 rounded-t px-4 py-2.5">
                  <h3 className="text-sm font-semibold tracking-wider text-white uppercase">
                    {group.name}
                  </h3>
                </div>

                <div className="border-hpi-light-gray rounded-b border border-t-0 bg-white">
                  {/* Header */}
                  <div className="border-hpi-light-gray grid grid-cols-[1fr_1fr_1fr_1fr_1fr] gap-4 border-b px-4 py-2">
                    <div className="text-hpi-active-blue text-xs font-medium">
                      Name
                    </div>
                    <div className="text-hpi-active-blue text-xs font-medium">
                      Title
                    </div>
                    <div className="text-hpi-active-blue text-xs font-medium">
                      Cell
                    </div>
                    <div className="text-hpi-active-blue text-xs font-medium">
                      Home
                    </div>
                    <div className="text-hpi-active-blue text-xs font-medium">
                      Email
                    </div>
                  </div>

                  {/* Rows */}
                  {group.employees.map((emp) => (
                    <div
                      key={emp.id}
                      className="border-hpi-light-gray/50 grid grid-cols-[1fr_1fr_1fr_1fr_1fr] gap-4 border-b px-4 py-3 last:border-b-0"
                    >
                      <div className="text-hpi-dark-gray text-sm font-medium">
                        {emp.name}
                      </div>
                      <div className="text-hpi-dark-gray text-sm">
                        {emp.job_title ?? ""}
                      </div>
                      <div>
                        {emp.cell_phone ? (
                          <a
                            href={`tel:${emp.cell_phone}`}
                            className="text-hpi-active-blue flex items-center gap-1 text-sm hover:underline"
                          >
                            <Smartphone className="h-3.5 w-3.5" />
                            {formatPhone(emp.cell_phone)}
                          </a>
                        ) : (
                          <span className="text-hpi-med-gray text-sm">-</span>
                        )}
                      </div>
                      <div>
                        {emp.home_phone ? (
                          <a
                            href={`tel:${emp.home_phone}`}
                            className="text-hpi-active-blue flex items-center gap-1 text-sm hover:underline"
                          >
                            <Phone className="h-3.5 w-3.5" />
                            {formatPhone(emp.home_phone)}
                          </a>
                        ) : (
                          <span className="text-hpi-med-gray text-sm">-</span>
                        )}
                      </div>
                      <div>
                        {emp.email ? (
                          <a
                            href={`mailto:${emp.email}`}
                            className="text-hpi-active-blue flex items-center gap-1 text-sm hover:underline"
                          >
                            <Mail className="h-3.5 w-3.5" />
                            <span className="truncate">{emp.email}</span>
                          </a>
                        ) : (
                          <span className="text-hpi-med-gray text-sm">-</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  )
}
